// Factory for grid column behaviors.
//
// Wraps user-provided hooks into AG Grid colDef callbacks, injecting
// `config` and `updateRow` so behavior authors don't touch the grid API.
//
// Supported hooks (mirror AG Grid's colDef callback names):
//   onCellValueChanged({ value, oldValue, newValue, data, node, config, updateRow })
//   valueFormatter({ value, data, config })  → string
//   valueParser({ newValue, oldValue, data, config })  → any
//   valueSetter({ newValue, oldValue, data, node, config })  → boolean
//   valueGetter({ data, node, config })  → any
//
// Static colDef keys (passed through directly):
//   cellEditor, cellEditorPopup
//
// Dynamic colDef keys (receive config):
//   cellEditorParams(config)  → object
//
// Usage:
//   import { createBehavior } from "./create_behavior"
//
//   export const currency = createBehavior({
//     valueFormatter({ value }) {
//       return value ? `$${Number(value).toFixed(2)}` : ""
//     },
//     valueParser({ newValue }) {
//       return newValue.replace(/[^0-9.]/g, "")
//     }
//   })
//
// The returned object has an `apply(config, updateRow)` method that
// returns a colDef fragment ready to merge.

// Hooks that get `config` and `updateRow` injected into their params.
const HOOKS = [
  "onCellValueChanged",
  "valueFormatter",
  "valueParser",
  "valueSetter",
  "valueGetter"
]

// Keys that pass through directly as colDef properties (no wrapping).
const PASSTHROUGH_KEYS = ["cellEditor", "cellEditorPopup"]

// Keys that are functions receiving `config` and returning a colDef value.
const CONFIG_KEYS = ["cellEditorParams"]

export function createBehavior(hooks) {
  return {
    apply(config, updateRow) {
      const colDefOverrides = {}

      for (const hook of HOOKS) {
        if (!hooks[hook]) continue

        colDefOverrides[hook] = (params) => {
          return hooks[hook]({
            ...params,
            config,
            updateRow
          })
        }
      }

      // Copy passthrough keys directly (e.g. cellEditor class references)
      for (const key of PASSTHROUGH_KEYS) {
        if (hooks[key] !== undefined) {
          colDefOverrides[key] = hooks[key]
        }
      }

      // Invoke config-dependent keys with the behavior config
      for (const key of CONFIG_KEYS) {
        if (typeof hooks[key] === "function") {
          colDefOverrides[key] = hooks[key](config)
        } else if (hooks[key] !== undefined) {
          colDefOverrides[key] = hooks[key]
        }
      }

      return colDefOverrides
    }
  }
}
